import { reactive } from "vue"
import { useOpsApi } from "src/api/client"
import type { IncidentDetails, IncidentGroupOverview, IncidentRef } from "src/generated/models"
import { useAuthStore } from "./auth"

const GROUPS_TTL_MS = 4 * 60 * 60_000
const REFS_TTL_MS = 60_000

/**
 * Header badge counts plus a cached incident-group list for id→title lookups. Counts are refreshed by
 * the app-init poll loop and cleared on lock-out.
 */
export interface IncidentStore {
  assignedCount: number
  relevantCount: number
  /**
   * The critical ones among [assignedCount] + [relevantCount] — the one figure allowed to colour the
   * start-page tile red.
   *
   * Counted against the sum of those two and **not** against all open incidents: the tile is a statement
   * about me, and an „N davon“ measured against a different whole reads as a contradiction, with N larger
   * than the number above it.
   */
  criticalCount: number
  /** internal: cached group list (reactive so id→title lookups in templates refresh once loaded). */
  cachedGroups: IncidentGroupOverview[] | null
  cachedGroupsAt: number
  /**
   * internal: per-number resolution cache for `#123` markdown references (reactive so rendered
   * markdown re-links once refs arrive). `null` = resolved but not found / not readable.
   */
  cachedRefs: Map<number, IncidentRef | null>
  cachedRefsAt: Map<number, number>
  groupTitleOrId(id: string): string
  listGroups(): Promise<IncidentGroupOverview[]>
  /** Looked-up `#number` ref: a ref, `null` (resolved-not-found), or `undefined` (not yet resolved). */
  refByNumber(number: number): IncidentRef | null | undefined
  /** Resolve the given `#numbers` (scoped — only fetches uncached/stale ones; dedupes callers). */
  resolveRefs(numbers: number[]): Promise<void>
  updateCounts(): Promise<void>
  clearCounts(): void
}

const store: IncidentStore = reactive<IncidentStore>({
  assignedCount: 0,
  relevantCount: 0,
  criticalCount: 0,
  cachedGroups: null,
  cachedGroupsAt: 0,
  cachedRefs: new Map(),
  cachedRefsAt: new Map(),

  groupTitleOrId(id: string): string {
    return this.cachedGroups?.find((g) => g.id === id)?.title ?? id
  },

  refByNumber(number: number): IncidentRef | null | undefined {
    return this.cachedRefs.get(number)
  },

  async resolveRefs(numbers: number[]): Promise<void> {
    const now = Date.now()
    const need = [...new Set(numbers)].filter((n) => {
      const at = this.cachedRefsAt.get(n)
      return at == null || now - at >= REFS_TTL_MS
    })
    if (need.length === 0) return
    // Stamp the fetch time up front so concurrent callers (multiple MarkdownDisplay instances on
    // one page) don't each fire the same request.
    for (const n of need) this.cachedRefsAt.set(n, now)
    try {
      const resp = await useOpsApi().incident.resolveRefs({ numbers: need })
      const found = new Map(resp.refs.map((r) => [r.number, r]))
      for (const n of need) this.cachedRefs.set(n, found.get(n) ?? null)
    } catch {
      // Clear the markers so a later render retries instead of caching the failure.
      for (const n of need) this.cachedRefsAt.delete(n)
    }
  },

  async listGroups(): Promise<IncidentGroupOverview[]> {
    if (this.cachedGroups && Date.now() - this.cachedGroupsAt < GROUPS_TTL_MS) {
      return this.cachedGroups
    }
    const resp = await useOpsApi().incident.listGroups()
    this.cachedGroups = resp.groups
    this.cachedGroupsAt = Date.now()
    return resp.groups
  },

  async updateCounts(): Promise<void> {
    if (useAuthStore().authenticated !== true) return
    try {
      // An ack means the incident is known about, so it must not keep driving the badge (the server
      // only sends ackedUntil while it lies in the future).
      const notAcked = (i: IncidentDetails) => !i.permaAcked && i.ackedUntil == null

      const assigned = await useOpsApi().incident.list({
        relevant: undefined,
        assigned: true,
        closed: false,
        categories: [],
        deviceIds: [],
        labelFilters: [],
        settingFilters: [],
        settingFiltersOnlyExplicit: false,
        factFilters: [],
        sensorIds: [],
      })
      const mine = assigned.incidents.filter(notAcked)
      this.assignedCount = mine.length

      const relevant = await useOpsApi().incident.list({
        relevant: true,
        assigned: undefined,
        closed: false,
        categories: [],
        deviceIds: [],
        labelFilters: [],
        settingFilters: [],
        settingFiltersOnlyExplicit: false,
        factFilters: [],
        sensorIds: [],
      })
      // relevant count excludes already-assigned incidents
      const mineByGroup = relevant.incidents.filter((i) => !i.assigned && notAcked(i))
      this.relevantCount = mineByGroup.length

      // The two sets are disjoint (the second excludes assigned ones), so the union counts each
      // incident exactly once.
      this.criticalCount = [...mine, ...mineByGroup].filter((i) => i.severity === "CRITICAL").length
    } catch {
      // ignore errors during polling
    }
  },

  clearCounts(): void {
    this.assignedCount = 0
    this.relevantCount = 0
    this.criticalCount = 0
    this.cachedRefs.clear()
    this.cachedRefsAt.clear()
  },
})

export function useIncidentStore(): IncidentStore {
  return store
}
